import React, { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, Copy, FolderPlus, Plus, Trash2 } from "lucide-react";
import { formatEuro, formatNumber, formatUnitEuro } from "../../utils/offerCalc";
import {
  appendChild, duplicateNode, insertAfter, moveNode, newGroup, newItem, removeNode, updateNode,
} from "./treeOps";

const parseNumber = (text) => {
  const value = Number(String(text).replace(/\s/g, "").replace(",", "."));
  return Number.isFinite(value) && value >= 0 ? value : null;
};

// Champ numérique tolérant à la virgule ; garde la saisie en cours tant qu'il a le focus.
// `optional` : vide = pas de valeur propre (null), le gris montre alors la valeur héritée.
const NumberInput = ({ value, onChange, disabled, label, width = "w-20", optional, placeholder = "0" }) => {
  const shown = (current) => (optional ? (current ?? "") : (current || "")).toString().replace(".", ",");
  const [text, setText] = useState(shown(value));
  const focused = useRef(false);
  useEffect(() => {
    if (!focused.current) setText(shown(value));
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <input
      type="text"
      inputMode="decimal"
      aria-label={label}
      disabled={disabled}
      value={text}
      placeholder={placeholder}
      onFocus={() => { focused.current = true; }}
      onBlur={() => {
        focused.current = false;
        setText(shown(value));
      }}
      onChange={(event) => {
        setText(event.target.value);
        const empty = event.target.value.trim() === "";
        const parsed = empty ? (optional ? null : 0) : parseNumber(event.target.value);
        if (parsed !== null || empty) onChange(parsed);
      }}
      className={`${width} rounded border border-transparent bg-transparent px-1.5 py-1 text-right tabular-nums placeholder:text-slate-300 hover:border-slate-200 focus:border-blue-400 focus:bg-white focus:outline-none disabled:hover:border-transparent`}
    />
  );
};

// TJM et coefficient d'achats propres à un lot, un groupe ou une ligne ; vides, ils héritent du niveau au-dessus.
const RateInput = ({ node, inherited, disabled, edit }) => (
  <NumberInput label="TJM propre (€ HT par jour)" width="w-16" optional value={node.day_rate ?? null}
    placeholder={formatNumber(inherited)} disabled={disabled} onChange={(value) => edit(node.id, "day_rate", value)} />
);
const MarginInput = ({ node, inherited, disabled, edit }) => (
  <NumberInput label="Coefficient d'achats propre" width="w-12" optional value={node.material_margin ?? null}
    placeholder={formatNumber(inherited)} disabled={disabled}
    onChange={(value) => edit(node.id, "material_margin", value > 0 && value <= 1 ? value : null)} />
);

const TextInput = ({ value, onChange, disabled, label, bold, autoFocus, onEnter }) => (
  <input
    type="text"
    aria-label={label}
    disabled={disabled}
    value={value || ""}
    autoFocus={autoFocus}
    onKeyDown={(event) => {
      if (event.key === "Enter" && onEnter) {
        event.preventDefault();
        onEnter();
      }
    }}
    onChange={(event) => onChange(event.target.value)}
    className={`w-full min-w-0 rounded border border-transparent bg-transparent px-1.5 py-1 hover:border-slate-200 focus:border-blue-400 focus:bg-white focus:outline-none disabled:hover:border-transparent ${bold ? "font-semibold text-slate-900" : "text-slate-800"}`}
  />
);

const IconButton = ({ title, onClick, children, danger }) => (
  <button
    type="button"
    title={title}
    aria-label={title}
    onClick={onClick}
    className={`rounded p-1 ${danger ? "text-slate-400 hover:bg-red-50 hover:text-red-600" : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"}`}
  >
    {children}
  </button>
);

const Actions = ({ readOnly, children }) => (
  <td className="w-40 whitespace-nowrap px-2 text-right">
    {!readOnly && <div className="flex justify-end gap-0.5 opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100">{children}</div>}
  </td>
);

const EstimateTable = ({ content, totals, readOnly, apply, focusId, setFocusId }) => {
  const params = totals.params;
  const hoursPerDay = Number(params.hours_per_day) || 0;
  const baseRate = (Number(params.hourly_rate) || 0) * hoursPerDay;
  const edit =(id, field, value) => apply((current) => updateNode(current, id, { [field]: value }), `${id}.${field}`);
  const act = (updater, focus) => {
    apply(updater);
    if (focus) setFocusId(focus);
  };

  const renderNodes = (nodes, depth) => nodes.flatMap((node) => {
    const t = totals.nodes[node.id] || { days: 0, purchase: 0, fixed: 0 };
    const isGroup = node.kind === "group";
    const indent = { paddingLeft: `${depth * 18 + 8}px` };
    const row = (
      <tr key={node.id} className={`group border-b border-slate-100 ${isGroup ? "bg-slate-50/70" : "bg-white"}`}>
        <td className="py-0.5 pr-2" style={indent}>
          <TextInput
            label={isGroup ? "Nom du sous-ensemble" : "Libellé de la ligne"}
            value={node.name}
            bold={isGroup}
            disabled={readOnly}
            autoFocus={focusId === node.id}
            onChange={(value) => edit(node.id, "name", value)}
            onEnter={isGroup ? undefined : () => {
              const next = newItem();
              act((current) => insertAfter(current, node.id, next), next.id);
            }}
          />
        </td>
        <td className="text-right">
          <NumberInput label="Quantité" width="w-14" value={node.quantity === 1 ? 1 : node.quantity} disabled={readOnly}
            onChange={(value) => edit(node.id, "quantity", value)} />
        </td>
        <td className="text-right">
          {isGroup
            ? <span className="px-1.5 font-medium tabular-nums text-slate-600">{formatNumber(t.days)}</span>
            : <NumberInput label="Jours" value={node.days} disabled={readOnly} onChange={(value) => edit(node.id, "days", value)} />}
        </td>
        <td className="px-1.5 text-right tabular-nums text-slate-400">{formatNumber(t.days * hoursPerDay)}</td>
        <td className="text-right">
          <RateInput node={node} inherited={t.day_rate ?? baseRate} disabled={readOnly} edit={edit} />
        </td>
        <td className="text-right">
          {isGroup
            ? <span className="px-1.5 font-medium tabular-nums text-slate-600">{t.purchase ? formatEuro(t.purchase) : ""}</span>
            : <NumberInput label="Coût d'achat" width="w-24" value={node.purchase} disabled={readOnly}
                onChange={(value) => edit(node.id, "purchase", value)} />}
        </td>
        <td className="text-right">
          <MarginInput node={node} inherited={t.material_margin ?? params.material_margin} disabled={readOnly} edit={edit} />
        </td>
        <td className="text-right">
          {isGroup
            ? <span className="px-1.5 font-medium tabular-nums text-slate-600">{t.fixed ? formatEuro(t.fixed) : ""}</span>
            : <NumberInput label="Forfait" width="w-24" value={node.fixed} disabled={readOnly}
                onChange={(value) => edit(node.id, "fixed", value)} />}
        </td>
        <td className={`px-2 text-right tabular-nums ${isGroup ? "font-semibold text-slate-800" : "text-slate-700"}`}>
          {formatEuro(t.net || 0)}
        </td>
        <Actions readOnly={readOnly}>
          {isGroup && (
            <>
              <IconButton title="Ajouter une ligne dans ce sous-ensemble" onClick={() => {
                const next = newItem();
                act((current) => appendChild(current, node.id, next), next.id);
              }}><Plus className="h-4 w-4" /></IconButton>
              <IconButton title="Ajouter un sous-groupe" onClick={() => {
                const next = newGroup("Sous-groupe");
                act((current) => appendChild(current, node.id, next), next.id);
              }}><FolderPlus className="h-4 w-4" /></IconButton>
            </>
          )}
          {!isGroup && (
            <IconButton title="Ajouter une ligne en dessous" onClick={() => {
              const next = newItem();
              act((current) => insertAfter(current, node.id, next), next.id);
            }}><Plus className="h-4 w-4" /></IconButton>
          )}
          <IconButton title="Monter" onClick={() => act((current) => moveNode(current, node.id, -1))}><ArrowUp className="h-4 w-4" /></IconButton>
          <IconButton title="Descendre" onClick={() => act((current) => moveNode(current, node.id, 1))}><ArrowDown className="h-4 w-4" /></IconButton>
          <IconButton title="Dupliquer" onClick={() => act((current) => duplicateNode(current, node.id))}><Copy className="h-4 w-4" /></IconButton>
          <IconButton title="Supprimer" danger onClick={() => act((current) => removeNode(current, node.id))}><Trash2 className="h-4 w-4" /></IconButton>
        </Actions>
      </tr>
    );
    return isGroup ? [row, ...renderNodes(node.children || [], depth + 1)] : [row];
  });

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="min-w-full text-sm">
        <thead className="sticky top-0 z-10 bg-slate-100 text-xs uppercase tracking-wide text-slate-500">
          <tr>
            <th className="min-w-[18rem] px-3 py-2 text-left">Libellé</th>
            <th className="px-2 py-2 text-right">Qté</th>
            <th className="px-2 py-2 text-right">Jours</th>
            <th className="px-2 py-2 text-right">Heures</th>
            <th className="px-2 py-2 text-right" title="TJM propre, en € HT par jour ; vide : celui du niveau au-dessus">TJM</th>
            <th className="px-2 py-2 text-right">Achats</th>
            <th className="px-2 py-2 text-right" title="Coefficient d'achats propre (prix = coût ÷ coefficient) ; vide : celui du niveau au-dessus">Coef.</th>
            <th className="px-2 py-2 text-right">Forfait</th>
            <th className="px-2 py-2 text-right">Prix</th>
            <th className="px-2 py-2" aria-label="Actions" />
          </tr>
        </thead>
        {(content.lots || []).map((lot, index) => {
          const lotTotals = totals.lots[index] || {};
          return (
            <tbody key={lot.id} className="border-b-4 border-slate-100">
              <tr className="group bg-blue-50/60">
                <td className="py-1.5 pl-2 pr-2" colSpan={2}>
                  <TextInput label="Nom du lot" value={lot.name} bold disabled={readOnly} autoFocus={focusId === lot.id}
                    onChange={(value) => edit(lot.id, "name", value)} />
                </td>
                <td colSpan={7} className="px-2 text-sm text-slate-600">
                  <div className="flex flex-wrap items-center justify-end gap-4">
                    <span className="inline-flex items-center gap-1"
                      title="Quantité produite par le lot (ex. 4 boîtiers) : multiplie tout le lot, saisi pour une unité">
                      Quantité
                      <NumberInput label="Quantité du lot" width="w-12" value={lot.quantity ?? 1} disabled={readOnly}
                        onChange={(value) => edit(lot.id, "quantity", value > 0 ? value : 1)} />
                      <input type="text" aria-label="Unité" placeholder="unité" disabled={readOnly} value={lot.unit || ""}
                        onChange={(event) => edit(lot.id, "unit", event.target.value.slice(0, 40))}
                        className="w-20 rounded border border-transparent bg-transparent px-1.5 py-1 placeholder:text-slate-300 hover:border-slate-200 focus:border-blue-400 focus:bg-white focus:outline-none" />
                    </span>
                    <span className="inline-flex items-center gap-1">
                      TJM
                      <RateInput node={lot} inherited={baseRate} disabled={readOnly} edit={edit} />
                    </span>
                    <span className="inline-flex items-center gap-1">
                      Coef.
                      <MarginInput node={lot} inherited={params.material_margin} disabled={readOnly} edit={edit} />
                    </span>
                    <label className="inline-flex items-center gap-1.5">
                      <input type="checkbox" disabled={readOnly} checked={Boolean(lot.optional)}
                        onChange={(event) => edit(lot.id, "optional", event.target.checked)} />
                      Option
                    </label>
                    <label className="inline-flex items-center gap-1.5">
                      <input type="checkbox" disabled={readOnly} checked={lot.include_pm !== false}
                        onChange={(event) => edit(lot.id, "include_pm", event.target.checked)} />
                      Gestion de projet
                    </label>
                    <span className="inline-flex items-center gap-1">
                      Délai
                      <NumberInput label="Délai en semaines" width="w-12" value={lot.delay_weeks || 0} disabled={readOnly}
                        onChange={(value) => edit(lot.id, "delay_weeks", value || null)} />
                      sem.
                    </span>
                  </div>
                </td>
                <Actions readOnly={readOnly}>
                  <IconButton title="Ajouter un sous-ensemble" onClick={() => {
                    const next = newGroup();
                    act((current) => appendChild(current, lot.id, next), next.id);
                  }}><FolderPlus className="h-4 w-4" /></IconButton>
                  <IconButton title="Ajouter une ligne" onClick={() => {
                    const next = newItem();
                    act((current) => appendChild(current, lot.id, next), next.id);
                  }}><Plus className="h-4 w-4" /></IconButton>
                  <IconButton title="Monter le lot" onClick={() => act((current) => moveNode(current, lot.id, -1))}><ArrowUp className="h-4 w-4" /></IconButton>
                  <IconButton title="Descendre le lot" onClick={() => act((current) => moveNode(current, lot.id, 1))}><ArrowDown className="h-4 w-4" /></IconButton>
                  <IconButton title="Dupliquer le lot" onClick={() => act((current) => duplicateNode(current, lot.id))}><Copy className="h-4 w-4" /></IconButton>
                  <IconButton title="Supprimer le lot" danger onClick={() => act((current) => removeNode(current, lot.id))}><Trash2 className="h-4 w-4" /></IconButton>
                </Actions>
              </tr>
              {renderNodes(lot.children || [], 1)}
              {lot.include_pm !== false && (
                <tr className="text-slate-500">
                  <td className="py-1 pl-7 italic">Gestion de projet ({formatNumber(Number(params.pm_rate) * 100)} % des jours)</td>
                  <td />
                  <td className="px-2 text-right tabular-nums">{formatNumber(lotTotals.pm_days)}</td>
                  <td className="px-2 text-right tabular-nums text-slate-400">{formatNumber(lotTotals.pm_days * hoursPerDay)}</td>
                  <td colSpan={4} />
                  <td className="px-2 text-right tabular-nums">{formatEuro(lotTotals.pm_labour)}</td>
                  <td />
                </tr>
              )}
              <tr className="bg-slate-50">
                <td className="py-2 pl-3 font-semibold text-slate-900" colSpan={2}>
                  Total {lot.name || `lot ${index + 1}`}{lot.optional ? " (option)" : ""}
                </td>
                <td className="px-2 text-right font-semibold tabular-nums">{formatNumber(lotTotals.days + lotTotals.pm_days)}</td>
                <td className="px-2 text-right tabular-nums text-slate-500">{formatNumber(lotTotals.hours)}</td>
                <td />
                <td className="px-2 text-right tabular-nums text-slate-500">{lotTotals.purchase ? formatEuro(lotTotals.purchase) : ""}</td>
                <td />
                <td className="px-2 text-right tabular-nums text-slate-500">{lotTotals.fixed ? formatEuro(lotTotals.fixed) : ""}</td>
                <td className="px-2 text-right text-base font-semibold tabular-nums text-blue-700">{formatEuro(lotTotals.net)}</td>
                <td className="px-2 text-right text-xs text-slate-500">
                  {lotTotals.quantity > 1 && (
                    <div className="font-medium text-slate-700">
                      {formatUnitEuro(lotTotals.unit_net)} par {lot.unit || "unité"}
                    </div>
                  )}
                  {Number(params.client_discount) > 0 ? `${formatEuro(lotTotals.gross)} HT avant remise` : ""}
                </td>
              </tr>
            </tbody>
          );
        })}
      </table>
    </div>
  );
};

export default EstimateTable;
